import {
  Breadcrumb,
  Button,
  ButtonProps,
  ChevronEndMediumIcon,
  Flex,
  Menu,
  Text,
} from "@fluentui/react-northstar";
import React, { Fragment, useMemo } from "react";
import { matchPath, useHistory, useLocation } from "react-router";

import { getPageContext } from "../utils/teamsHelpers";
import { PollyIcon, PollyIconName } from "./PollyIcon";

export type BreadcrumbLink = {
  title: React.ReactNode;
  icon?: React.ReactNode;
  link?: string;
  onClick?: (event: React.MouseEvent) => void;
};

export type NavigationItem = {
  key: string;
  label: React.ReactNode;
  description: React.ReactNode;
  icon: PollyIconName;
  link: string;
};

type Props = {
  breadcrumbs?: BreadcrumbLink[];
  navigationItems?: NavigationItem[];
  actions?: Array<ButtonProps & { key: string }>;
};

export const NavigationHeader: React.FC<Props> = ({ actions, breadcrumbs, navigationItems }) => {
  const history = useHistory();
  const location = useLocation();

  const activeNavItem = useMemo(() => {
    if (!navigationItems) {
      return undefined;
    }

    const activeItem = navigationItems.find(n => matchPath(location.pathname, n.link));
    return activeItem ?? navigationItems[0];
  }, [location, navigationItems]);

  if (!breadcrumbs && !navigationItems && !actions) {
    return null;
  }

  return (
    <Flex column gap="gap.small">
      <Flex wrap vAlign="center" gap="gap.large">
        {!!navigationItems?.length && (
          <Flex.Item>
            <Menu
              className="navbarnew__content"
              iconOnly
              items={[
                {
                  key: "selected-nav-item",
                  content: activeNavItem?.label,
                  icon: activeNavItem?.icon ? (
                    <PollyIcon icon={activeNavItem.icon} inheritColor />
                  ) : undefined,
                  menu: {
                    items: navigationItems.map(item => ({
                      key: item.key,
                      onClick: () => history.push(item.link),
                      icon: <PollyIcon icon={item.icon} inheritColor />,
                      content: (
                        <Flex column gap="gap.smaller">
                          {item.label}
                          {item.description}
                        </Flex>
                      ),
                    })),
                  },
                },
              ]}
            />
          </Flex.Item>
        )}

        {breadcrumbs && (
          <Flex.Item>
            <Breadcrumb>
              {breadcrumbs.map((crumb, index) => (
                <Fragment key={index}>
                  {index > 0 && (
                    <Breadcrumb.Divider>
                      <ChevronEndMediumIcon />
                    </Breadcrumb.Divider>
                  )}
                  <Breadcrumb.Item>
                    <Breadcrumb.Link href={crumb.link} onClick={crumb.onClick}>
                      <Flex gap="gap.smaller" vAlign="center">
                        {crumb.icon}
                        <Text content={crumb.title} weight="bold" />
                      </Flex>
                    </Breadcrumb.Link>
                  </Breadcrumb.Item>
                </Fragment>
              ))}
            </Breadcrumb>
          </Flex.Item>
        )}

        {actions && (
          <Flex.Item push>
            <Flex gap="gap.smaller">
              {actions.map(action => (
                <Button {...action} />
              ))}
            </Flex>
          </Flex.Item>
        )}
      </Flex>
    </Flex>
  );
};

export const ChannelContentNavigationItem: NavigationItem = {
  key: "channel-content",
  label: <Text content="All Content: General" weight="bold" color="brand" />,
  description: <Text content="Everything created in this channel" weight="bold" />,
  link: `${getPageContext()}/content/tab/team`,
  icon: "all-channel-content-icon",
};

export const YourChannelContentNavigationItem: NavigationItem = {
  key: "your-channel-content",
  label: <Text content="Your Content: General" weight="bold" color="brand" />,
  description: (
    <Text
      content="All content you have created or has been shared with you in this channel"
      weight="bold"
    />
  ),
  link: `${getPageContext()}/content/tab/personal`,
  icon: "your-content-icon",
};

export const YourContentOrgNavigationItem = (link?: string) =>
  ({
    key: "your-org-content",
    label: <Text content="Your Content: Organization Wide" weight="bold" color="brand" />,
    description: (
      <Text
        content="All content you have created or has been shared with you across all teams"
        weight="bold"
      />
    ),
    link: link || `${getPageContext()}/content/tab/tenant`,
    icon: "all-your-content-icon",
  } as NavigationItem);

export const YourMeetingContentNavigationItem = (link?: string) =>
  ({
    key: "meetings-content",
    label: <Text content="Meetings" weight="bold" color="brand" />,
    description: (
      <Text
        content="All content you have created or has been shared with you in meetings"
        weight="bold"
      />
    ),
    link: link || `${getPageContext()}/content/tab/meetings`,
    icon: "Meetings-content-icon",
  } as NavigationItem);

export const TemplateNavigationItem: NavigationItem = {
  key: "templates",
  label: <Text content="Templates" weight="bold" color="brand" />,
  description: <Text content="All your Polly templates" weight="bold" />,
  link: `${getPageContext()}/content/tab/templates`,
  icon: "my-templates-icon",
};

export const ChannelTabNavigationItems: NavigationItem[] = [
  ChannelContentNavigationItem,
  YourChannelContentNavigationItem,
  YourContentOrgNavigationItem(),
  YourMeetingContentNavigationItem(),
  TemplateNavigationItem,
];
